import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Dialog from '../ui/Dialog';
import Field, { FormErrorSummary, FormServerError, rules, useFormValidation, type FormSchema } from '../ui/Field';
import EntityPicker from '../ui/EntityPicker';
import type { FieldRule } from '../ui/fieldModel';
import { FormFooter } from '../projects/formBits';
import { AdditionalAssigneesCombobox } from '../tasks/TaskAssigneesField';
import { api } from '../../services/api';
import { formatIls } from '../sales/shared';
import type { Characterization } from '../../types';
import { installmentSplit, linkableCharacterizationQuotes } from './characterizationModel';

const nonNegative: FieldRule = (v) => {
  const s = String(v ?? '').trim();
  if (!s) return null;
  const n = Number(s.replace(',', '.'));
  return Number.isFinite(n) && n >= 0 ? null : { key: 'engagementForm.nonNegative' };
};

/**
 * P-07 / D-77 — the "+ אפיון" window (the customer is locked: it opens from
 * the "אפיונים והצעות" tab).
 *
 *   שם האפיון*            מחיר האפיון* (לפני מע"מ) → 2 × 50%
 *   הצעת מחיר מקושרת      עובדים משויכים            הערות
 *
 * Save = ONE request: the card, its task "אפיון — <שם>" and the two
 * installments; the customer becomes "באפיון" (D-17 / D-74 §5).
 */
export default function CharacterizationFormModal({
  organizationId,
  organizationName,
  cards,
  onClose,
  onSaved,
}: {
  organizationId: string;
  organizationName?: string;
  /** The customer's cards (a quote that already has one is not offered). */
  cards: readonly Characterization[];
  onClose: () => void;
  onSaved: (card: Characterization | null) => void;
}) {
  const { t } = useTranslation();
  const [values, setValues] = useState({ name: '', price: '', quoteId: '', notes: '' });
  const [assigneeIds, setAssigneeIds] = useState<string[]>([]);
  const set = (k: keyof typeof values, v: string) => setValues((p) => ({ ...p, [k]: v }));

  const { data: quotesRes } = useQuery({
    queryKey: ['quotes', { organization_id: organizationId }],
    queryFn: () => api.getQuotes({ organization_id: organizationId, limit: 100 }),
    staleTime: 30_000,
  });
  const quotes = useMemo(() => linkableCharacterizationQuotes(quotesRes?.data ?? [], cards), [quotesRes, cards]);

  const { data: agentsRes } = useQuery({
    queryKey: ['assignable-agents', organizationId],
    queryFn: () => api.getAssignableAgents(organizationId),
    staleTime: 60_000,
  });
  const agents = useMemo(() => agentsRes?.data ?? [], [agentsRes]);

  const schema: FormSchema = {
    'ch-name': [rules.required(), rules.minLength(2)],
    'ch-price': [rules.required(), nonNegative],
  };
  const v = useFormValidation(schema, { 'ch-name': values.name, 'ch-price': values.price });
  const labels: Record<string, string> = {
    'ch-name': t('characterization.form.name'),
    'ch-price': t('characterization.form.price'),
  };

  const priceNum = Number(values.price.replace(',', '.'));
  const split = values.price.trim() ? installmentSplit(priceNum) : null;

  const mutation = useMutation({
    mutationFn: async () => {
      const r = await api.createCharacterization({
        organization_id: organizationId,
        title: values.name.trim(),
        price: priceNum,
        quote_id: values.quoteId || null,
        assignee_ids: assigneeIds,
        notes: values.notes.trim() || null,
      });
      return r.data ?? null;
    },
    onSuccess: (card) => onSaved(card),
  });
  const submit = v.submit(() => mutation.mutate());

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('characterization.form.title', { customer: organizationName ?? '' })}
      size="lg"
      dismissible={!mutation.isPending}
      footer={<FormFooter formId="characterization-form" busy={mutation.isPending} submitLabel={t('characterization.form.create')} onCancel={onClose} />}
    >
      <form id="characterization-form" onSubmit={submit} noValidate className="space-y-5">
        {v.showSummary && <FormErrorSummary errors={v.errors} labels={labels} order={Object.keys(schema)} />}
        <FormServerError error={mutation.error} fallback={t('characterization.form.createError')} />

        <div className="grid gap-x-4 gap-y-4 sm:grid-cols-2">
          <Field id="ch-name" label={labels['ch-name']} required error={v.errors['ch-name']}>
            <input className="input" value={values.name} onChange={(e) => set('name', e.target.value)} onBlur={() => v.blur('ch-name')} autoComplete="off" />
          </Field>
          <Field
            id="ch-price"
            label={labels['ch-price']}
            required
            error={v.errors['ch-price']}
            hint={split ? t('characterization.form.split', { first: formatIls(split[0]), second: formatIls(split[1]) }) : t('characterization.form.priceHint')}
          >
            <input type="number" inputMode="decimal" min={0} step="0.01" className="input" value={values.price}
              onChange={(e) => set('price', e.target.value)} onBlur={() => v.blur('ch-price')} />
          </Field>
        </div>

        <Field id="ch-quote" label={t('characterization.form.quote')} optional
          hint={quotes.length === 0 ? t('characterization.form.noQuotes') : undefined}>
          <EntityPicker
            value={values.quoteId}
            onChange={(id) => set('quoteId', id)}
            disabled={quotes.length === 0}
            emptyLabel={t('characterization.form.noQuote')}
            options={quotes.map((q) => ({ value: q.id, label: `#${q.quote_number} · ${q.title}`, keywords: String(q.quote_number) }))}
          />
        </Field>

        <AdditionalAssigneesCombobox agents={agents} ownerId="" selectedIds={assigneeIds} onChange={setAssigneeIds} label={t('characterization.form.assignees')} />

        <Field id="ch-notes" label={t('characterization.form.notes')} optional>
          <textarea className="input" rows={3} value={values.notes} onChange={(e) => set('notes', e.target.value)} />
        </Field>

        <p className="text-xs text-surface-600">{t('characterization.form.onSave')}</p>
      </form>
    </Dialog>
  );
}
